import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/app/common/common-components/ui/Dialog";
import { Button } from "@/app/common/common-components/ui/Button";
import { Loader, Star, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiAgent from "@/app/api/apiAgent";
import { toast } from "react-toastify";
import { FavouritesResponseModel, FavouriteItem } from "@/app/models/FavouritesResponseModel";
import { Link } from "react-router";

interface FavoritesDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const FavoritesDialog: React.FC<FavoritesDialogProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();

  const { data: favoritesData, isLoading } = useQuery<FavouritesResponseModel>({
    queryKey: ["favorites"],
    queryFn: async () => await apiAgent.Home.getFavouritesAjax(),
    enabled: isOpen,
  });

  const { mutate: resetFavorite } = useMutation({
    mutationFn: apiAgent.Home.resetFavouriteAjax,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favorites"] });
      toast.success("Favorite removed successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to remove favorite");
    },
  });

  const handleResetFavorite = (moduleID: number, dataRef: string) => {
    const formData = new FormData();
    formData.append("ID", "#");
    formData.append("Module", moduleID.toString());
    formData.append("DataRef", dataRef);
    resetFavorite(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="tw-sm:tw-max-w-md">
        <DialogHeader>
          <DialogTitle className="tw-flex tw-items-center tw-gap-2">
            <Star className="tw-h-5 tw-w-5 tw-text-yellow-500" />
            Favorites
          </DialogTitle>
        </DialogHeader>

        <div className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-max-h-60 tw-overflow-y-auto">
            {isLoading || !favoritesData ? (
              <div className="tw-flex tw-justify-center tw-py-8">
                <Loader className="tw-h-6 tw-w-6 tw-animate-spin tw-text-gray-400" />
              </div>
            ) : favoritesData?.data && favoritesData.data.length > 0 ? (
              <ul className="tw-divide-y tw-divide-gray-200">
                {favoritesData.data.map((item: FavouriteItem) => (
                  <li key={`${item.module}-${item.dataRef}`} className="tw-py-2">
                    <div className="tw-flex tw-items-center tw-justify-between tw-rounded-md tw-px-2 tw-py-1 hover:tw-bg-gray-50">
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <i className={`fa ${item.typeIcon} tw-text-gray-500`}></i>
                        <div>
                          <Link to={item.url} className="tw-text-sm tw-font-medium tw-text-gray-900 hover:tw-text-primary">
                            {item.name}
                          </Link>
                          <span className="tw-text-xs tw-text-gray-500">
                            {" "}
                            in{" "}
                            <Link to={item.parentUrl} className="hover:tw-text-primary">
                              {item.parentName}
                            </Link>
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleResetFavorite(parseInt(item.module), item.dataRef)}
                        className="tw-rounded-full tw-p-1 hover:tw-bg-gray-100"
                        title="Remove from favorites"
                      >
                        <Trash2 className="tw-h-4 tw-w-4 tw-text-gray-400 hover:tw-text-red-500" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="tw-py-4 tw-text-center tw-text-gray-500">No favorites found</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FavoritesDialog;
